import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import { useNavigate, useParams } from 'react-router-dom';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import NoticeEditor from '../components/notice/NoticeEditor';
import { ActionProps, useApp } from '../contextProviders/AppProvider';
import { useData } from '../contextProviders/DataProvider';
import useOrg from '../dataHooks/useOrg';
import { useNoticeMutators } from '../dataMutators/useNoticeMutators';
import DefaultObjects from '../types/DefaultObjects';
import Notice, { NOTICE_CONTEXT_TYPES } from '../types/Notice';
import { generateFirestoreId } from '../utils/utils';

export default function NoticeCreatePage(): ReactElement {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { setPageName } = useApp();
  const { canCreateNotices, userState } = useData();
  const { addNotice } = useNoticeMutators();
  const { org, isOrgLoading, orgError } = useOrg(orgId ?? null);

  const [pageActions, setPageActions] = useState<ActionProps[] | null>(null);

  useEffect(() => {
    if (!orgId) {
      setPageName('Create Notice');
    } else if (org) {
      setPageName(`Create ${org.name} Notice`);
    }
  }, [setPageName, orgId, org]);

  // Pre-generate the document ID
  const notice = useMemo((): Notice => {
    const docId = generateFirestoreId();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 0, 0); // End of day tomorrow

    return {
      ...DefaultObjects.Notice,
      itemId: docId,
      creatorId: userState.userProfile?.itemId || '',
      orgId: org?.itemId || '',
      orgName: org?.name || '',
      contextPairs: org ? [{
        id: org.itemId,
        name: org.name,
        type: NOTICE_CONTEXT_TYPES.ORG
      }] : [],
      displayStopsAt: Timestamp.fromDate(tomorrow),
    };
  }, [org, userState.userProfile?.itemId]);

  const handleCreate = useCallback((newNotice: Notice) => {
    addNotice(newNotice).then((addedNotice) => {
      if (addedNotice) {
        navigate(`/notice/${addedNotice.orgId}/${addedNotice.itemId}`, {
          replace: true,
        });
      } else {
        console.error('Notice not added');
        navigate(-1);
      }
    });
  }, [addNotice, navigate]);

  const handleActionsChange = useCallback((actions: ActionProps[] | null) => {
    setPageActions(actions);
  }, []);

  const validateUser = () => {
    return canCreateNotices(orgId) ?? false;
  };

  return (
    <GratiPageWrapper
      validateUser={validateUser}
      isContentLoading={isOrgLoading}
      contentError={orgError}
      pageActions={pageActions}
    >
      {notice && (
        <NoticeEditor
          notice={notice}
          isNew
          handleCreate={handleCreate}
          onActionsChange={handleActionsChange}
        />
      )}
    </GratiPageWrapper>
  );
}
